/**
 * `/m/classes` 班级与邀请码 (ACCOUNTS.md §2 学生入口, docs/ROADMAP.md A1).
 *
 * Students self-register with a class invite code; the course team rotates or
 * stops a code from here. Rotating invalidates the old code immediately, so it
 * sits behind a confirmation.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  EmptyState,
  Field,
  Icon,
  Input,
  Sheet,
  Table,
  useConfirmSafe,
  useToast,
} from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { classesApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { CopyField, PageHeader, QueryBoundary, StatusTag } from "./common";
import { MembersSheet } from "./MembersSheet";
import type { ClassItem } from "./types";

export function ClassesPage() {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();
  const { me } = useAuth();
  const canManage = (me?.user?.roles ?? []).some(
    (r) => r === "teacher" || r === "course_team" || r === "admin",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [codeFor, setCodeFor] = useState<ClassItem | null>(null);
  const [membersFor, setMembersFor] = useState<ClassItem | null>(null);

  const list = useQuery({ queryKey: qk.classes, queryFn: ({ signal }) => classesApi.list(signal) });

  const rotate = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { invite_enabled?: boolean } }) =>
      classesApi.rotateInvite(id, patch),
    onSuccess: (r, vars) => {
      toast.success(
        vars.patch.invite_enabled === false
          ? "已停用该班级的邀请码"
          : vars.patch.invite_enabled === true
            ? "已重新启用邀请码"
            : "邀请码已轮换，旧码立即失效",
      );
      setCodeFor(r.class);
      void qc.invalidateQueries({ queryKey: qk.classes });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const askRotate = async (c: ClassItem) => {
    const ok = await confirm({
      title: `轮换 ${c.name} 的邀请码？`,
      message: "轮换之后旧邀请码立即失效，已经拿到旧码但还没注册的学生需要重新拿新码。",
      confirmLabel: "轮换",
      danger: true,
    });
    if (ok) rotate.mutate({ id: c.id, patch: {} });
  };

  const askDisable = async (c: ClassItem) => {
    if (!c.invite_enabled) {
      rotate.mutate({ id: c.id, patch: { invite_enabled: true } });
      return;
    }
    const ok = await confirm({
      title: `停用 ${c.name} 的邀请码？`,
      message: "停用之后没有人能再用这个码注册；已注册的学生不受影响。随时可以重新启用。",
      confirmLabel: "停用",
      danger: true,
    });
    if (ok) rotate.mutate({ id: c.id, patch: { invite_enabled: false } });
  };

  const columns: Array<Column<ClassItem>> = [
    { key: "name", header: "班级", render: (c) => c.name },
    { key: "id", header: "编号", render: (c) => <span className="xz-adm__mono">{c.id}</span> },
    {
      key: "teachers",
      header: "任课教师",
      render: (c) =>
        !c.teachers || c.teachers.length === 0 ? (
          <span className="gz-muted">未指派</span>
        ) : (
          <span className="xz-adm__tags">
            {c.teachers.map((t) => (
              <StatusTag key={t.id} icon="ui-school">
                {t.display_name}
              </StatusTag>
            ))}
          </span>
        ),
    },
    {
      key: "students",
      header: "学生",
      align: "end",
      hideOnMobile: true,
      render: (c) => (
        <span>
          {c.student_count ?? "—"}
          {c.unverified_count ? (
            <span className="gz-caption"> · 待核验 {c.unverified_count}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "code",
      header: "邀请码",
      render: (c) =>
        c.invite_code ? (
          <span className="xz-code xz-adm__mono">
            <Icon id="ui-hash" size={16} />
            {c.invite_code}
          </span>
        ) : (
          <span className="gz-muted">未生成</span>
        ),
    },
    {
      key: "state",
      header: "状态",
      render: (c) =>
        c.invite_enabled ? (
          <StatusTag icon="ui-check-circle" tone="success">
            可注册
          </StatusTag>
        ) : (
          <StatusTag icon="ui-ban" tone="warning">
            已停用
          </StatusTag>
        ),
    },
    {
      key: "actions",
      header: "操作",
      render: (c) => (
        <span className="xz-adm__rowactions">
          <Button size="sm" variant="secondary" onClick={() => setMembersFor(c)}>
            成员
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!c.invite_code}
            onClick={() => setCodeFor(c)}
          >
            复制邀请码
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={rotate.isPending}
            onClick={() => void askRotate(c)}
          >
            轮换
          </Button>
          <Button
            size="sm"
            variant={c.invite_enabled ? "danger" : "secondary"}
            loading={rotate.isPending}
            onClick={() => void askDisable(c)}
          >
            {c.invite_enabled ? "停用" : "启用"}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="xz-adm">
      <PageHeader
        title="班级"
        description="学生用班级邀请码自助注册。老师和标注专家用「成员」里的链接加入，链接可以多次使用，也可以随时撤销。"
        actions={
          <Button
            leadingIcon={<Icon id="ui-school" size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            新建班级
          </Button>
        }
      />

      <QueryBoundary
        isPending={list.isPending}
        isError={list.isError}
        error={list.error}
        onRetry={() => void list.refetch()}
      >
        <Table
          caption="班级列表"
          columns={columns}
          rows={list.data?.classes ?? []}
          rowKey={(c) => c.id}
          empty={
            <EmptyState
              title="还没有班级"
              description="先建一个班级，再把邀请码发到课程群里。"
              action={<Button onClick={() => setCreateOpen(true)}>新建班级</Button>}
            />
          }
        />
      </QueryBoundary>

      <CreateClassSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={setCodeFor}
      />

      {membersFor && (
        <MembersSheet
          classItem={membersFor}
          selfId={me?.user.id ?? null}
          canManage={canManage}
          onClose={() => setMembersFor(null)}
        />
      )}

      {codeFor && (
        <Sheet
          open
          onClose={() => setCodeFor(null)}
          title={`${codeFor.name} 的邀请码`}
          footer={
            <Button variant="secondary" onClick={() => setCodeFor(null)}>
              关闭
            </Button>
          }
        >
          <div className="xz-form">
            {codeFor.invite_code ? (
              <>
                <CopyField
                  label="班级邀请码"
                  value={codeFor.invite_code}
                  hint="学生在注册页填这个码。轮换之后旧码立即失效。"
                />
                <CopyField
                  label="注册链接"
                  value={`${globalThis.location?.origin ?? ""}/register?code=${encodeURIComponent(codeFor.invite_code)}`}
                  hint="发到课程群里，学生打开即可自助注册。"
                />
              </>
            ) : (
              <p className="gz-muted">这个班级还没有邀请码，点「轮换」生成一个。</p>
            )}
            {!codeFor.invite_enabled && (
              <div className="gz-notice" role="status">
                <Icon id="ui-warning" size={20} />
                <span>这个班级的邀请码当前处于停用状态，学生暂时无法用它注册。</span>
              </div>
            )}
          </div>
        </Sheet>
      )}
    </div>
  );
}

function CreateClassSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (c: ClassItem) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [id, setId] = useState("");
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () =>
      classesApi.create({ ...(id.trim() ? { id: id.trim() } : {}), name: name.trim() }),
    onSuccess: (r) => {
      toast.success(`班级 ${r.class.name} 已创建`);
      void qc.invalidateQueries({ queryKey: qk.classes });
      setId("");
      setName("");
      onClose();
      onCreated(r.class);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="新建班级"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={name.trim() === ""}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            创建
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <Field label="班级名称" required hint="例如 声乐二班">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="班级编号" hint="留空则由服务端生成，例如 vocal2">
          <Input value={id} onChange={(e) => setId(e.target.value)} placeholder="vocal2" />
        </Field>
        <p className="gz-caption">创建后会自动生成一个邀请码，可以随时轮换或停用。</p>
      </div>
    </Sheet>
  );
}
